/**
 * ProductivityBar — what the AI gave back, last 30 days.
 *
 * Restyled onto the KPI-strip standard: the headline figures are `KpiTile`s in
 * a divided `GlassCard`, and the per-surface breakdown is a column of `Well`s
 * beside them instead of a row of MUI `Tooltip`s. The tooltips were the real
 * problem — the detail they hid ("~4h saved across 12 matters") is the only
 * reason the breakdown is interesting, and it was reachable only by hovering,
 * which is to say not reachable at all on a tablet.
 *
 * IT IS HALF A ROW WIDE NOW, beside Recent Activity. Full width made it a
 * letterbox: three numbers stretched across twelve columns with a five-across
 * strip of wells underneath. So the body is TWO COLUMNS — figures on the left,
 * breakdown on the right — which is what a half-width card can actually hold,
 * and it stacks below `lg` like everything else.
 *
 * THE WINDOW IS A BADGE, not a subtitle, matching Firm Economics: the period a
 * card covers is state, and state belongs beside the title. Unlike economics
 * it is not yet a CONTROL — `/dashboard/summary` computes a fixed trailing 30
 * days and takes no date parameters, so a picker here would move nothing.
 *
 * Currency: `cost_saved` is an estimate the backend computes in the org's own
 * currency, and the summary payload does not name it. It is rendered as a
 * plain grouped number with a translated label rather than a hard `$`, which
 * is what it used to print for everyone including the euro-zone.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiBriefcaseLight,
  PiCalendarBlankLight,
  PiChatCircleDotsLight,
  PiClockLight,
  PiFileMagnifyingGlassLight,
  PiFileTextLight,
  PiTableLight,
  PiTrendUpLight,
} from 'react-icons/pi';
import { DashboardProductivity } from '../../../store/reducer/DashboardReducer';
import GlassCard from '../../shared/brand/GlassCard';
import Badge from '../../shared/TailwindComponents/Badge';
import { KpiTile, Well } from '../../shared/brand/Field';

interface ProductivityBarProps {
  productivity: DashboardProductivity | null;
}

const ProductivityBar: React.FC<ProductivityBarProps> = ({ productivity }) => {
  const { t } = useTranslation();

  if (!productivity || (productivity.hours_saved === 0 && productivity.tasks_completed_30d === 0)) {
    return null;
  }

  const breakdown = [
    { key: 'matters', icon: <PiBriefcaseLight />, label: t('Matters'), data: productivity.breakdown.matters },
    { key: 'reviews', icon: <PiFileMagnifyingGlassLight />, label: t('Reviews'), data: productivity.breakdown.reviews },
    { key: 'drafts', icon: <PiFileTextLight />, label: t('Drafts'), data: productivity.breakdown.drafts },
    { key: 'research', icon: <PiChatCircleDotsLight />, label: t('Research'), data: productivity.breakdown.research },
    { key: 'tables', icon: <PiTableLight />, label: t('Tables'), data: productivity.breakdown.tables },
  ].filter((item) => (item.data?.count ?? 0) > 0);

  return (
    <GlassCard
      icon={<PiTrendUpLight />}
      title={t('Productivity')}
      actions={
        <Badge color="gray" icon={<PiCalendarBlankLight className="h-3 w-3" />}>
          {t('Last {{count}} days', { count: 30 })}
        </Badge>
      }
      padding="none"
      className="overflow-hidden"
      fill
      bodyClassName="flex min-h-0 flex-1 flex-col"
      data-testid="productivity"
    >
      {/*
        Two columns from `lg` up — figures beside their explanation. One column
        below it, where the card is the full width of a phone anyway.
      */}
      {/* Gaps rather than rules, as in the other metric strips: a cross drawn
          through a card makes its inside read as a table of cells. */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-1 p-1 lg:grid-cols-2">
        <div className="grid auto-rows-fr grid-cols-1 gap-1">
          <KpiTile
            icon={<PiClockLight />}
            value={`${productivity.hours_saved}h`}
            label={t('Hours saved')}
            hint={t('Across every AI surface')}
            tone="success"
          />
          <KpiTile
            icon={<PiTrendUpLight />}
            value={productivity.cost_saved.toLocaleString()}
            label={t('Estimated cost saved')}
            hint={t('In your organisation’s currency')}
          />
          <KpiTile
            value={productivity.tasks_completed_30d.toLocaleString()}
            label={t('Tasks completed')}
            hint={t('In the same window')}
          />
        </div>

        <div className="custom-scrollbar-main flex min-h-0 flex-col gap-2 overflow-y-auto p-3">
          {breakdown.length === 0 ? (
            <p className="text-xs text-fg-muted">{t('No AI activity to break down yet.')}</p>
          ) : (
            breakdown.map((item) => (
              <Well key={item.key} className="flex flex-shrink-0 items-center gap-2">
                <span className="flex-shrink-0 text-fg-muted [&>svg]:h-4 [&>svg]:w-4">{item.icon}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-700 tabular-nums dark:text-gray-200">
                    {item.data.count} {item.label}
                  </span>
                  <span className="block truncate text-[11px] text-gray-400 dark:text-gray-500" title={item.data.detail}>
                    {t('~{{hours}}h saved', { hours: item.data.hours })}
                  </span>
                </span>
              </Well>
            ))
          )}
        </div>
      </div>
    </GlassCard>
  );
};

export default ProductivityBar;
